import React, { useState, useRef, useEffect } from 'react';
import {
    useQuickSearchStore,
    TOOL_DEFINITIONS,
    ToolDefinition,
    ToolField,
    ToolCallResult,
    UnifiedResultItem,
    UnifiedToolResponse,
    extractLinksFromResult,
    LinkAction
} from '../../stores/quickSearchStore';
import type { DictFinding } from '../../types';
import { formatDateTime } from '../../utils/formatDateTime';

// =============================================================================
// PROPS
// =============================================================================

interface QuickResearchProps {
    /** When provided, each result shows a [+ Save Finding] button */
    onSaveFinding?: (finding: DictFinding) => void;
    /** Compact mode for workspace tab context */
    compact?: boolean;
    /** Optional query routed from the landing page assistant */
    initialQuery?: string;
    /** Clear the routed query after it has been auto-submitted once */
    onInitialQueryHandled?: () => void;
}

// =============================================================================
// FIELD INPUT COMPONENT
// =============================================================================

function FieldInput({ field, value, onChange }: { field: ToolField; value: any; onChange: (value: any) => void }) {
    if (field.type === 'checkbox') {
        return (
            <label className="flex items-center space-x-2 text-sm text-fg-secondary cursor-pointer">
                <input
                    type="checkbox"
                    checked={!!value}
                    onChange={(e) => onChange(e.target.checked)}
                    className="rounded border-border-default bg-surface-sunken text-accent focus:ring-accent"
                />
                <span>{field.description || field.name}</span>
            </label>
        );
    }

    if (field.type === 'select' && field.options) {
        return (
            <div>
                <label className="block text-xs font-medium text-fg-secondary mb-1">
                    {field.description || field.name}
                    {field.required && <span className="text-danger ml-1">*</span>}
                </label>
                <select
                    value={value || field.defaultValue || ''}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full px-3 py-2 bg-surface-sunken border border-border-default rounded text-sm text-fg focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
                >
                    {field.options.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>
        );
    }

    return (
        <div>
            <label className="block text-xs font-medium text-fg-secondary mb-1">
                {field.description || field.name}
                {field.required && <span className="text-danger ml-1">*</span>}
            </label>
            <input
                type={field.type === 'number' ? 'number' : 'text'}
                value={value || ''}
                onChange={(e) => onChange(field.type === 'number' ? parseFloat(e.target.value) || '' : e.target.value)}
                placeholder={field.placeholder}
                step={field.type === 'number' ? '0.1' : undefined}
                className="w-full px-3 py-2 bg-surface-sunken border border-border-default rounded text-sm text-fg placeholder-fg-tertiary focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent"
            />
        </div>
    );
}

// =============================================================================
// TOOL TAB COMPONENT
// =============================================================================

interface ToolTabProps {
    tool: ToolDefinition;
    isActive: boolean;
    isLoading: boolean;
    onSelect: () => void;
    onSubmit: (params: Record<string, any>) => void;
}

function ToolTab({ tool, isActive, isLoading, onSelect, onSubmit }: ToolTabProps) {
    const [formValues, setFormValues] = useState<Record<string, any>>(() => {
        const defaults: Record<string, any> = {};
        tool.fields.forEach(field => {
            if (field.defaultValue !== undefined) {
                defaults[field.name] = field.defaultValue;
            } else if (field.type === 'checkbox') {
                defaults[field.name] = false;
            } else {
                defaults[field.name] = '';
            }
        });
        return defaults;
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const params: Record<string, any> = {};
        tool.fields.forEach(field => {
            const value = formValues[field.name];
            if (field.required || (value !== '' && value !== undefined && value !== null)) {
                params[field.name] = value;
            }
        });
        onSubmit(params);
    };

    const handleFieldChange = (fieldName: string, value: any) => {
        setFormValues(prev => ({ ...prev, [fieldName]: value }));
    };

    const canSubmit = tool.fields
        .filter(f => f.required)
        .every(f => formValues[f.name] !== '' && formValues[f.name] !== undefined);

    const categoryColors: Record<string, string> = {
        search: 'text-link',
        document: 'text-success',
        intelligence: 'text-node-definition',
        analysis: 'text-warning',
    };

    return (
        <div className="border-b border-border-default last:border-b-0">
            <button
                onClick={onSelect}
                className={`w-full px-4 py-3 text-left flex items-center justify-between transition-colors ${
                    isActive
                        ? 'bg-link-bg border-l-2 border-accent'
                        : 'hover:bg-surface-sunken/50'
                }`}
            >
                <div>
                    <div className="text-sm font-medium text-fg flex items-center gap-2">
                        {tool.label}
                        <span className={`text-[10px] font-normal ${categoryColors[tool.category] || 'text-fg-tertiary'}`}>
                            {tool.category}
                        </span>
                    </div>
                    <div className="text-xs text-fg-secondary mt-0.5">{tool.description}</div>
                </div>
                <svg
                    className={`w-4 h-4 text-fg-secondary transition-transform flex-shrink-0 ${isActive ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {isActive && (
                <form onSubmit={handleSubmit} className="px-4 py-3 bg-surface-sunken/30 space-y-3">
                    {tool.fields.map(field => (
                        <FieldInput
                            key={field.name}
                            field={field}
                            value={formValues[field.name]}
                            onChange={(value) => handleFieldChange(field.name, value)}
                        />
                    ))}
                    <button
                        type="submit"
                        disabled={!canSubmit || isLoading}
                        className={`w-full py-2 px-4 rounded text-sm font-medium transition-colors ${
                            canSubmit && !isLoading
                                ? 'bg-primary hover:bg-primary-hover text-on-primary transition-colors'
                                : 'bg-border-default text-fg-secondary cursor-not-allowed'
                        }`}
                    >
                        {isLoading ? 'Running...' : 'Run tool'}
                    </button>
                </form>
            )}
        </div>
    );
}

// =============================================================================
// UNIFIED RESULT ITEM CARD
// =============================================================================

interface UnifiedResultItemCardProps {
    item: UnifiedResultItem;
    index: number;
    onLinkClick: (link: LinkAction) => void;
    onSaveFinding?: (finding: DictFinding) => void;
}

function UnifiedResultItemCard({ item, index, onLinkClick, onSaveFinding }: UnifiedResultItemCardProps) {
    const [expanded, setExpanded] = useState(index < 3); // auto-expand first 3

    const handleSaveFinding = () => {
        if (!onSaveFinding) return;
        const finding: DictFinding = {
            finding_id: crypto.randomUUID(),
            source_document_celex: item.document_id,
            source_document_name: item.document_name,
            source_reference: item.reference,
            source_reference_hierarchy: item.hierarchy,
            source_text: item.content,
            source_snippet: item.content?.substring(0, 200),
            tool_used: item.source_tool,
            extracted_at: new Date().toISOString(),
            source_type: item.source_service === 'graph' ? 'eurlex' : 'pdf',
            document_type: item.document_type,
            document_full_title: item.document_full_title,
            article_title: item.article_title,
            source_service: item.source_service,
            source_url: item.source_url,
        };
        onSaveFinding(finding);
    };

    const score = item.rrf_score ?? item.relevance_score;
    const scorePercent = Math.round(score * 100);

    return (
        <div className="border border-border-default rounded-lg bg-bg/50 overflow-hidden">
            {/* Header */}
            <div
                className="px-3 py-2 flex items-center justify-between cursor-pointer hover:bg-surface-sunken/30"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    {/* Source badge */}
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${
                        item.source_service === 'graph'
                            ? 'bg-success-bg text-success border border-success'
                            : 'bg-link-bg text-link border border-link'
                    }`}>
                        {item.source_service === 'graph' ? 'EU Reg' : 'PDF'}
                    </span>
                    {/* Document + Reference */}
                    <span className="text-xs text-fg-secondary flex-shrink-0">{item.document_name}</span>
                    {item.reference && item.reference !== 'Document Catalog' && item.reference !== 'Table of Contents' && item.reference !== 'AI Analysis' && (
                        <span className="text-sm font-medium text-fg truncate">{item.reference}</span>
                    )}
                    {item.article_title && (
                        <span className="text-xs text-fg-secondary truncate">— {item.article_title}</span>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                    {score > 0 && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            scorePercent >= 80 ? 'bg-success-bg text-success' :
                            scorePercent >= 60 ? 'bg-warning-bg text-warning' :
                            'bg-surface-sunken text-fg-secondary'
                        }`}>
                            {scorePercent}%
                        </span>
                    )}
                    <svg
                        className={`w-3.5 h-3.5 text-fg-tertiary transition-transform ${expanded ? 'rotate-180' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </div>

            {expanded && (
                <div className="border-t border-border-default">
                    {/* Hierarchy */}
                    {item.hierarchy && (
                        <div className="px-3 py-1 text-[10px] text-fg-tertiary bg-bg-warm/30">
                            {item.hierarchy}
                        </div>
                    )}

                    {/* Content */}
                    <div className="px-3 py-2">
                        <pre className="text-xs text-fg-secondary whitespace-pre-wrap font-sans leading-relaxed max-h-64 overflow-y-auto">
                            {item.content}
                        </pre>
                    </div>

                    {/* Concepts */}
                    {item.concepts && item.concepts.length > 0 && (
                        <div className="px-3 py-2 border-t border-border-default/50">
                            <div className="flex flex-wrap gap-1">
                                {item.concepts.map((concept, i) => (
                                    <button
                                        key={i}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onLinkClick({ label: concept, targetTool: 'get_concept_map', params: { concept_name: concept } });
                                        }}
                                        className="text-[10px] px-1.5 py-0.5 bg-[color:var(--bark-purple-light)] text-node-definition border border-node-definition rounded hover:bg-[color:var(--bark-purple-light)] transition-colors"
                                    >
                                        {concept}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Cross-references */}
                    {((item.references && item.references.length > 0) || (item.cited_by && item.cited_by.length > 0)) && (
                        <div className="px-3 py-2 border-t border-border-default/50 text-[10px] text-fg-secondary space-y-1">
                            {item.references && item.references.length > 0 && (
                                <div className="flex flex-wrap items-center gap-1">
                                    <span className="text-fg-tertiary">Cites:</span>
                                    {item.references.slice(0, 5).map((ref, i) => (
                                        <button
                                            key={i}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onLinkClick({ label: ref, targetTool: 'get_content', params: { document_id: item.document_id, path: ref } });
                                            }}
                                            className="px-1 py-0.5 bg-surface-sunken rounded hover:bg-border-default text-fg-secondary transition-colors"
                                        >
                                            {ref}
                                        </button>
                                    ))}
                                    {item.references.length > 5 && <span>+{item.references.length - 5} more</span>}
                                </div>
                            )}
                            {item.cited_by && item.cited_by.length > 0 && (
                                <div className="flex flex-wrap items-center gap-1">
                                    <span className="text-fg-tertiary">Cited by:</span>
                                    {item.cited_by.slice(0, 5).map((ref, i) => (
                                        <button
                                            key={i}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onLinkClick({ label: ref, targetTool: 'get_content', params: { document_id: item.document_id, path: ref } });
                                            }}
                                            className="px-1 py-0.5 bg-surface-sunken rounded hover:bg-border-default text-fg-secondary transition-colors"
                                        >
                                            {ref}
                                        </button>
                                    ))}
                                    {item.cited_by.length > 5 && <span>+{item.cited_by.length - 5} more</span>}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Source URL + Save Finding */}
                    <div className="px-3 py-2 border-t border-border-default/50 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            {item.source_url && (
                                <a
                                    href={item.source_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[10px] text-link hover:text-link underline"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    View source
                                </a>
                            )}
                            <span className="text-[10px] text-fg-tertiary">{item.source_tool}</span>
                        </div>
                        {onSaveFinding && (
                            <button
                                onClick={(e) => { e.stopPropagation(); handleSaveFinding(); }}
                                className="text-xs px-2 py-1 bg-success-bg hover:bg-success-bg text-success border border-success rounded transition-colors"
                            >
                                + Save finding
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// =============================================================================
// RESULT CARD COMPONENT (wraps a full tool call response)
// =============================================================================

interface ResultCardProps {
    result: ToolCallResult;
    onLinkClick: (link: LinkAction) => void;
    onSaveFinding?: (finding: DictFinding) => void;
}

function ResultCard({ result, onLinkClick, onSaveFinding }: ResultCardProps) {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [showParams, setShowParams] = useState(false);
    const [showRaw, setShowRaw] = useState(false);

    const toolResponse = result.response;
    const links = result.status === 'success' ? extractLinksFromResult(result.tool, toolResponse) : [];
    const toolDef = TOOL_DEFINITIONS.find(t => t.name === result.tool);

    const formatTimestamp = (date: Date) => {
        return formatDateTime(date, { context: 'time' });
    };

    return (
        <div className={`border rounded-lg overflow-hidden ${
            result.status === 'error'
                ? 'border-danger bg-danger-bg'
                : result.status === 'loading'
                    ? 'border-warning bg-warning-bg'
                    : 'border-border-default bg-bg'
        }`}>
            {/* Header */}
            <div
                className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-surface-sunken/30"
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <div className="flex items-center space-x-3">
                    {result.status === 'loading' ? (
                        <div className="w-2 h-2 bg-warning rounded-full animate-pulse" />
                    ) : result.status === 'error' ? (
                        <div className="w-2 h-2 bg-danger rounded-full" />
                    ) : (
                        <div className="w-2 h-2 bg-success rounded-full" />
                    )}
                    <div>
                        <span className="text-sm font-medium text-fg">
                            {toolDef?.label || result.tool}
                        </span>
                        <span className="text-xs text-fg-tertiary ml-2">
                            {formatTimestamp(result.timestamp)}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {toolResponse?.sources_queried && toolResponse.sources_queried.length > 0 && (
                        <span className="text-[10px] text-fg-tertiary">
                            {toolResponse.sources_queried.join(' + ')}
                        </span>
                    )}
                    <svg
                        className={`w-4 h-4 text-fg-secondary transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </div>

            {/* Content */}
            {!isCollapsed && (
                <div className="border-t border-border-default">
                    {/* Parameters toggle */}
                    <button
                        onClick={() => setShowParams(!showParams)}
                        className="w-full px-4 py-2 text-left text-xs text-fg-secondary hover:bg-surface-sunken/30 flex items-center"
                    >
                        <svg
                            className={`w-3 h-3 mr-1 transition-transform ${showParams ? 'rotate-90' : ''}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        Parameters
                    </button>
                    {showParams && (
                        <pre className="px-4 py-2 text-xs text-fg-secondary bg-bg-warm/50 overflow-x-auto">
                            {JSON.stringify(result.params, null, 2)}
                        </pre>
                    )}

                    {/* Response */}
                    {result.status === 'loading' ? (
                        <div className="px-4 py-8 flex items-center justify-center">
                            <div className="animate-spin w-6 h-6 border-2 border-border-default border-t-accent rounded-full" />
                            <span className="ml-3 text-sm text-fg-secondary">Running tool...</span>
                        </div>
                    ) : result.status === 'error' ? (
                        <div className="px-4 py-3 text-sm text-danger">
                            Error: {result.error}
                        </div>
                    ) : (
                        <>
                            {/* Result count + raw toggle */}
                            {toolResponse && (
                                <div className="px-4 py-2 border-b border-border-default flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs text-fg-secondary">
                                            Found <span className="font-medium text-fg-secondary">{toolResponse.count}</span> results
                                        </span>
                                        {toolResponse.partial && (
                                            <span className="text-[10px] text-warning bg-warning-bg px-1.5 py-0.5 rounded">
                                                partial — some sources failed
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => setShowRaw(!showRaw)}
                                        className="text-xs text-fg-tertiary hover:text-fg-secondary"
                                    >
                                        {showRaw ? 'Show formatted' : 'Show raw JSON'}
                                    </button>
                                </div>
                            )}

                            {/* Errors from services */}
                            {toolResponse?.errors && toolResponse.errors.length > 0 && (
                                <div className="px-4 py-2 bg-danger-bg border-b border-danger">
                                    {toolResponse.errors.map((err, i) => (
                                        <div key={i} className="text-xs text-danger">
                                            {err.service && <span className="font-medium">[{err.service}] </span>}
                                            {err.error}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Results */}
                            <div className="px-4 py-3">
                                {showRaw ? (
                                    <pre className="text-xs text-fg-secondary bg-bg-warm/50 rounded p-3 overflow-auto max-h-96 whitespace-pre-wrap">
                                        {JSON.stringify(toolResponse, null, 2)}
                                    </pre>
                                ) : toolResponse?.results && toolResponse.results.length > 0 ? (
                                    <div className="space-y-2">
                                        {toolResponse.results.map((item, i) => (
                                            <UnifiedResultItemCard
                                                key={i}
                                                item={item}
                                                index={i}
                                                onLinkClick={onLinkClick}
                                                onSaveFinding={onSaveFinding}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-sm text-fg-tertiary text-center py-4">
                                        No results found.
                                    </div>
                                )}
                            </div>

                            {/* Link Actions */}
                            {links.length > 0 && (
                                <div className="px-4 py-3 border-t border-border-default">
                                    <div className="text-xs text-fg-tertiary mb-2">Quick actions:</div>
                                    <div className="flex flex-wrap gap-2">
                                        {links.slice(0, 10).map((link, i) => (
                                            <button
                                                key={i}
                                                onClick={() => onLinkClick(link)}
                                                className="px-2 py-1 text-xs bg-link-bg hover:bg-link-bg text-link rounded border border-link transition-colors"
                                            >
                                                {link.label}
                                            </button>
                                        ))}
                                        {links.length > 10 && (
                                            <span className="px-2 py-1 text-xs text-fg-tertiary">
                                                +{links.length - 10} more
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function QuickResearch({
    onSaveFinding,
    compact,
    initialQuery,
    onInitialQueryHandled,
}: QuickResearchProps) {
    const {
        activeTool,
        results,
        isLoading,
        setActiveTool,
        executeToolCall,
        clearResults
    } = useQuickSearchStore();

    const resultsPanelRef = useRef<HTMLDivElement>(null);
    const lastAutoQueryRef = useRef<string | null>(null);

    const scrollToTop = () => {
        resultsPanelRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        const normalizedQuery = initialQuery?.trim();
        if (!normalizedQuery || normalizedQuery === lastAutoQueryRef.current) return;

        lastAutoQueryRef.current = normalizedQuery;
        setActiveTool('search_content');
        executeToolCall('search_content', { query: normalizedQuery });
        onInitialQueryHandled?.();
        scrollToTop();
    }, [initialQuery, executeToolCall, onInitialQueryHandled, setActiveTool]);

    const handleToolSelect = (toolName: string) => {
        setActiveTool(activeTool === toolName ? null : toolName);
    };

    const handleToolSubmit = (toolName: string, params: Record<string, any>) => {
        executeToolCall(toolName, params);
        scrollToTop();
    };

    const handleLinkClick = (link: LinkAction) => {
        const toolDef = TOOL_DEFINITIONS.find(t => t.name === link.targetTool);
        const missingRequired = toolDef?.fields
            .filter(f => f.required)
            .some(f => !link.params[f.name]);

        if (missingRequired) {
            setActiveTool(link.targetTool);
        } else {
            executeToolCall(link.targetTool, link.params);
            scrollToTop();
        }
    };

    return (
        <div className="h-full bg-bg flex flex-col">
            {/* Header - hidden in compact mode */}
            {!compact && (
                <div className="border-b border-border-default px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center">
                        <svg className="w-5 h-5 text-fg-secondary mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <h1 className="text-lg font-semibold text-fg">Regulatory Search</h1>
                    </div>
                    {results.length > 0 && (
                        <button
                            onClick={clearResults}
                            className="text-xs text-fg-secondary hover:text-fg transition-colors"
                        >
                            Clear history
                        </button>
                    )}
                </div>
            )}

            {/* Beta disclaimer */}
            <div className="px-4 py-2 bg-warning-bg border-b border-warning flex items-center gap-2">
                <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded bg-warning text-white uppercase tracking-wide">Beta</span>
                <span className="text-xs text-warning">This feature is currently in testing. Results may be incomplete or inaccurate.</span>
            </div>

            {/* Main Content - Split View */}
            <div className="flex-1 flex overflow-hidden">
                {/* Left Panel - Tool Tabs */}
                <div className={`${compact ? 'w-72' : 'w-80'} border-r border-border-default overflow-y-auto bg-bg/50 flex-shrink-0`}>
                    {compact && (
                        <div className="px-3 py-2 border-b border-border-default flex items-center justify-between">
                            <span className="text-xs font-medium text-fg-secondary">Regulatory search tools</span>
                            {results.length > 0 && (
                                <button onClick={clearResults} className="text-[10px] text-fg-tertiary hover:text-fg-secondary">
                                    Clear
                                </button>
                            )}
                        </div>
                    )}
                    {!compact && (
                        <div className="p-4 border-b border-border-default">
                            <h2 className="text-sm font-semibold text-fg-secondary">Available tools</h2>
                            <p className="text-xs text-fg-tertiary mt-1">
                                Same tools as the research agent — explore regulatory data directly
                            </p>
                        </div>
                    )}
                    <div>
                        {TOOL_DEFINITIONS.map(tool => (
                            <ToolTab
                                key={tool.name}
                                tool={tool}
                                isActive={activeTool === tool.name}
                                isLoading={isLoading}
                                onSelect={() => handleToolSelect(tool.name)}
                                onSubmit={(params) => handleToolSubmit(tool.name, params)}
                            />
                        ))}
                    </div>
                </div>

                {/* Right Panel - Results */}
                <div ref={resultsPanelRef} className="flex-1 overflow-y-auto bg-bg-warm/30">
                    {results.length === 0 ? (
                        <div className="h-full flex items-center justify-center p-8">
                            <div className="text-center max-w-md">
                                <div className="w-16 h-16 bg-surface-sunken rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-fg-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-medium text-fg-secondary mb-2">Search regulatory data</h3>
                                <p className="text-sm text-fg-tertiary">
                                    Select a tool and run a query to explore EU regulations, EBA guidelines, and other regulatory documents.
                                    {onSaveFinding && ' Results can be saved as findings for your research.'}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className={`${compact ? 'p-3' : 'p-4'} space-y-4`}>
                            {results.map(result => (
                                <ResultCard
                                    key={result.id}
                                    result={result}
                                    onLinkClick={handleLinkClick}
                                    onSaveFinding={onSaveFinding}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
